/**
 * An offer's city and puja scope as multi-selects (admin doc 25 §3.2 item 3).
 *
 * **Built from the doc, not a design** (owner ruling 2026-10-26). The console has no combobox
 * multi-select, so each scope is the chosen names as chips (press one to drop it) and a select
 * that adds the next. Nothing chosen is everywhere, and the field says so rather than sitting
 * empty. The names come from `GET /v1/admin/offers/scope-options`, because the growth role
 * cannot read the geo or catalogue consoles.
 *
 * The server stores the list whole (`city_ids` / `puja_ids`) and freezes it at the first
 * redemption, like every other term.
 */
import { Chip } from '../components/primitives';
import { Select } from '../components/forms';
import { ErrorState } from '../components/feedback';
import { errorMessage } from '../lib/api/client';
import { useOfferScopeOptions, type OfferScopeOption } from '../lib/api/hooks/offers';

/** Each option's label, with the id only where two share a name (two Aurangabads). */
export function optionLabels(options: readonly OfferScopeOption[]): Map<number, string> {
  const seen = new Map<string, number>();
  for (const o of options) seen.set(o.name, (seen.get(o.name) ?? 0) + 1);
  return new Map(options.map((o) => [o.id, seen.get(o.name)! > 1 ? `${o.name} · #${o.id}` : o.name]));
}

/** "Patna, Gaya" — or the everywhere phrase for an empty scope; an unlisted id reads as `#id`. */
export function scopeSummary(ids: readonly number[], options: readonly OfferScopeOption[] | undefined, everywhere: string): string {
  if (ids.length === 0) return everywhere;
  const labels = optionLabels(options ?? []);
  return ids.map((id) => labels.get(id) ?? `#${id}`).join(', ');
}

/** Order-blind equality, for "did the operator change the scope?". */
export function sameIds(a: readonly number[], b: readonly number[]): boolean {
  const x = [...a].sort((m, n) => m - n);
  const y = [...b].sort((m, n) => m - n);
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

export function ScopePicker({
  label,
  everywhere,
  options,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  everywhere: string;
  options: readonly OfferScopeOption[];
  value: readonly number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}) {
  const labels = optionLabels(options);
  const remaining = options.filter((o) => !value.includes(o.id));
  return (
    <div style={{ display: 'grid', gap: 'var(--sp-xs)' }}>
      <Select
        density="admin"
        label={label}
        placeholder={value.length === 0 ? `${everywhere} — add one to narrow` : 'Add another…'}
        options={remaining.map((o) => labels.get(o.id)!)}
        value=""
        disabled={disabled}
        onChange={(picked) => {
          const option = remaining.find((o) => labels.get(o.id) === picked);
          if (option) onChange([...value, option.id]);
        }}
      />
      {value.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--sp-xs)', flexWrap: 'wrap' }} aria-label={`${label} chosen`}>
          {value.map((id) => (
            <Chip
              key={id}
              variant="filter"
              selected
              disabled={disabled}
              label={`${labels.get(id) ?? `#${id}`} ×`}
              onClick={() => onChange(value.filter((v) => v !== id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function OfferScopeFields({
  cityIds,
  pujaIds,
  onCities,
  onPujas,
  disabled = false,
}: {
  cityIds: readonly number[];
  pujaIds: readonly number[];
  onCities: (ids: number[]) => void;
  onPujas: (ids: number[]) => void;
  disabled?: boolean;
}) {
  const options = useOfferScopeOptions();
  if (options.isError) {
    return <ErrorState inline title="Could not read the cities and pujas" cause={errorMessage(options.error)} />;
  }
  if (!options.data) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-md)' }}>
      <ScopePicker
        label="Cities"
        everywhere="Every launched city"
        options={options.data.cities}
        value={cityIds}
        onChange={onCities}
        disabled={disabled}
      />
      <ScopePicker
        label="Pujas"
        everywhere="Every puja"
        options={options.data.pujas}
        value={pujaIds}
        onChange={onPujas}
        disabled={disabled}
      />
    </div>
  );
}
